<template>
  <section
    ref="textSectionEl"
    :class="[
      'text-section',
      {
        home: isHome,
        page: !isHome,
        grey,
        overflow: overflow && sectionCroppedHeight,
        top: top && overflow,
        'no-lines': noLines,
      },
    ]"
    :style="{
      '--section-height': sectionCroppedHeight + 'px',
      '--overflow-amount': overflowAmount + 'px',
    }"
  >
    <component
      v-if="sideComponent"
      class="text-section__side-component"
      :is="textComponents[sideComponent.component]"
      v-bind="sideComponent.content"
      isSideComponent
    />

    <div class="text-section__inner-content">
      <component
        v-for="component of components"
        :key="component.id"
        :is="textComponents[component.component]"
        v-bind="component.content"
        @vnodeMounted="childMounted"
        inTextSection
      />
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useElementBounding } from "@vueuse/core";

import TextBlock from "../general/TextBlock.vue";
import TextQuote from "../general/TextQuote.vue";
import TextTitle from "../general/TextTitle.vue";

const textComponents = {
  TextBlock,
  TextQuote,
  TextTitle,
};

const props = defineProps({
  components: {
    type: Array,
    default: () => [],
  },
  sideComponent: Object,
  currentPage: String,
  grey: Boolean,
  noLines: Boolean,
  overflow: Boolean,
  top: Boolean,
});

const overflowAmount = 100;
const textSectionEl = ref(null);
const sectionCroppedHeight = ref(0);
const mountedChildren = ref([]);

const { height } = useElementBounding(textSectionEl);

const isHome = computed(() => props.currentPage === "home");

watch(
  () => mountedChildren.value,
  () => {
    if (
      props.overflow &&
      mountedChildren.value.length === props.components.length
    ) {
      sectionCroppedHeight.value = height.value - overflowAmount;
    }
  }
);

function childMounted() {
  mountedChildren.value = [...mountedChildren.value, true];
}
</script>

<style lang="scss">
.text-section + .text-section {
    .text-section__inner-content, .text-section__side-component {
      padding-top: 0 !important;
    }
  }
</style>

<style lang="scss" scoped>
.text-section {
  position: relative;
  padding-left: calc(var(--page-padding-h) + var(--left-space));
  padding-right: var(--page-padding-h);
  background-color: var(--white-100);
  z-index: 1;
  width: 100%; // We set this mostly so that it is never larger!

  

  &.home:not(.no-lines),
  .mobile &,
  .tablet & {
    &::before {
      content: "";
      position: absolute;
      width: 1rem;
      height: 100%;
      left: var(--page-padding-h);
      border-left: 1px solid var(--page-color, var(--scala-grigi-gray-3));
    }
  }

  &.grey {
    background-color: var(--scala-grigi-gray-5);

    .mobile & {
      border-top: 1px solid var(--scala-grigi-gray-2);
    }

    .text-section__inner-content {
      background-color: var(--scala-grigi-gray-5);
    }

    .text-section__inner-content > * {
      background-color: var(--scala-grigi-gray-5);
    }
  }

  &.no-lines {
    --page-color: transparent;

    &.grey {
      --page-color: var(--scala-grigi-gray-5);
    }
  }

  &.overflow {
    .desktop &,
    .large & {
      height: var(--section-height);
      overflow: visible;

      .text-section__inner-content {
        border-bottom: 1px solid var(--page-color, var(--scala-grigi-gray-3));
      }
    }

    &.top {
      .desktop &,
      .large & {
        .text-section__inner-content {
          transform: translateY(calc(var(--overflow-amount) * -1));
          border-top: 1px solid var(--page-color, var(--scala-grigi-gray-3));
        }
      }
    }
  }

  &__side-component {
    padding-top: 3vh;

    .desktop &,
    .large & {
      position: absolute;
      left: var(--page-padding-h);
      max-width: var(--left-space);
      top: toRem(50);
      padding-right: 1rem;
    }

    .mobile &,
    .tablet & {
      position: relative;
      border-right: 1px solid var(--page-color, var(--scala-grigi-gray-3));
      padding-top: 5vh;
      padding-bottom: 0;
    }
  }

  &__inner-content {
    background-color: var(--white-100);
    border-left: 1px solid var(--page-color, var(--scala-grigi-gray-3));

    .page & {
      border-right: 1px solid var(--page-color, var(--scala-grigi-gray-3));
    }

    .desktop &,
    .large & {
      padding-top: 3vh;
    }

    .mobile &,
    .tablet & {
      border-right: 1px solid var(--page-color, var(--scala-grigi-gray-3));
      border-left: none;
      padding-bottom: 1.5rem;
    }

    > * {
      background-color: var(--white-100);

      &:not(:first-child) {
        .desktop &,
        .large & {
          padding-top: 1.5rem;
        }
      }

      &:not(:last-child) {
        .desktop &,
        .large & {
          padding-bottom: 1.5rem;
        }
      }
    }
  }
}
</style>
